import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import {
    fetchExecutionRecord,
    getExecutionRecordSteps,
    fetchTestCaseDetails,
    fetchMCPToolsList,
} from '../../../utils/api';
import { fetchEnvironmentList } from '../../../utils/api/environment_apis';
import { getExecutionFilesForDropdown } from '../../../utils/api/executionFileApis';
import { getVariableListData } from '../../../utils/api/Variable_apis';
import { useOrganizationServiceUrl } from '../../../hooks/useOrganizationServiceUrl';
import { setVariablesList, setVariablesListDescriptions } from '../../replayslice';
import {
    loadPlanFromTestcase,
    setSavedPlanLoading,
    setMcpToolsList,
    setFileSuggestions,
    setSelectedEnvironment,
    startAutomation,
} from '../automationStudioNewSlice';
import {
    parseInstructionsFromApi,
    recoverInstructionsFromPlanSteps,
} from '../parseAutomationInstructions';
import {
    mergeExecutionRecordIntoInstructions,
    extractDynamicResultFromPlan,
} from '../executionRecordLoader';
import { shouldSkipSavedPlanReload } from '../planUtils';

/**
 * Loads environments, MCP tools, variables, file suggestions, and saved test case plans.
 */
export default function useAutomationStudioNewInit({ projectId, testCaseId }) {
    const dispatch = useDispatch();
    const store = useStore();
    const { organizationData } = useOrganizationServiceUrl();
    const { agentServiceUrl } = organizationData || {};
    const [environments, setEnvironments] = useState([]);
    const [testCaseKey, setTestCaseKey] = useState(null);
    const [selectedTestCaseOption, setSelectedTestCaseOption] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const loadSharedResources = useCallback(async () => {
        const [envResult, mcpResult, varResult, fileResult] = await Promise.all([
            fetchEnvironmentList(),
            fetchMCPToolsList({ url: agentServiceUrl }),
            getVariableListData(),
            getExecutionFilesForDropdown(),
        ]);

        if (envResult.status === 1 && envResult.data) {
            const formatted = envResult.data.map((env) => ({
                value: env.id,
                label: env.name,
            }));
            setEnvironments(formatted);
            const savedEnv = localStorage.getItem('automation_studio_environment');
            if (savedEnv && formatted.some((env) => env.value === savedEnv)) {
                dispatch(setSelectedEnvironment(savedEnv));
            }
        }

        if (mcpResult?.status === 1 && Array.isArray(mcpResult.data)) {
            dispatch(setMcpToolsList(mcpResult.data));
        }

        if (varResult?.status === 1) {
            dispatch(setVariablesList(varResult.data?.data || []));
            dispatch(setVariablesListDescriptions(varResult.data?.description || {}));
        }

        if (fileResult?.status === 1 && fileResult.data) {
            dispatch(
                setFileSuggestions({
                    suggestions: fileResult.data.suggestions || [],
                    descriptions: fileResult.data.descriptions || {},
                })
            );
        }
    }, [dispatch, agentServiceUrl]);

    const loadTestCase = useCallback(
        async (id) => {
            dispatch(setSavedPlanLoading(true));
            try {
                const [instructionsResult, planResult, tcDetailResult] = await Promise.all([
                    fetchExecutionRecord({ testCaseId: id }),
                    getExecutionRecordSteps({ testCaseId: id }),
                    fetchTestCaseDetails({ testCaseId: id }),
                ]);

                if (tcDetailResult.status === 1 && tcDetailResult.data?.id) {
                    setTestCaseKey(tcDetailResult.data.key);
                    setSelectedTestCaseOption(tcDetailResult.data);
                } else {
                    setSelectedTestCaseOption(null);
                }

                let parsedInstructions = [];
                if (instructionsResult.status === 1 && instructionsResult.data?.automation_instruction != null) {
                    parsedInstructions = parseInstructionsFromApi(
                        instructionsResult.data.automation_instruction
                    );
                }

                const plan = planResult.status === 1 ? planResult.data?.plan : null;

                if (parsedInstructions.length === 0 && plan?.steps?.length > 0) {
                    parsedInstructions = recoverInstructionsFromPlanSteps(plan.steps);
                }

                if (parsedInstructions.length === 0) {
                    parsedInstructions = [{ id: Date.now(), text: '', aiSteps: [] }];
                }

                const mergedInstructions = plan
                    ? mergeExecutionRecordIntoInstructions(parsedInstructions, plan)
                    : parsedInstructions;

                dispatch(
                    loadPlanFromTestcase({
                        instructions: mergedInstructions,
                        dynamicResult: extractDynamicResultFromPlan(plan),
                        executionRecordPlan: plan ?? null,
                        testCaseId: id,
                        testCaseKey: tcDetailResult.data?.key ?? null,
                        automationStatus: tcDetailResult.data?.automation_status ?? null,
                    })
                );
            } catch (error) {
                console.error('[AutomationStudioNew] Failed to load test case:', error);
                dispatch(startAutomation());
            } finally {
                dispatch(setSavedPlanLoading(false));
            }
        },
        [dispatch]
    );

    useEffect(() => {
        if (!projectId) return undefined;

        let cancelled = false;
        setIsInitialLoading(true);
        loadSharedResources().finally(() => {
            if (!cancelled) setIsInitialLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [projectId, loadSharedResources]);

    useEffect(() => {
        if (!projectId) return undefined;

        if (!testCaseId) {
            dispatch(setSavedPlanLoading(false));
            setTestCaseKey(null);
            setSelectedTestCaseOption(null);
            return undefined;
        }

        const studioState = store.getState()?.automationStudioNew;
        if (shouldSkipSavedPlanReload(studioState, testCaseId)) {
            const savedKey = studioState?.savedPlan?.testCaseKey;
            if (savedKey) {
                setTestCaseKey(savedKey);
            }
            setSelectedTestCaseOption((prev) => {
                if (prev?.id && String(prev.id) === String(testCaseId)) return prev;
                if (!testCaseId) return null;
                return {
                    id: testCaseId,
                    key: savedKey || testCaseId,
                    title: savedKey || testCaseId,
                };
            });
            dispatch(setSavedPlanLoading(false));
            return undefined;
        }

        dispatch(setSavedPlanLoading(true));
        setSelectedTestCaseOption(null);
        loadTestCase(testCaseId);
    }, [projectId, testCaseId, loadTestCase, dispatch, store]);

    return {
        environments,
        testCaseKey,
        selectedTestCaseOption,
        isInitialLoading,
        reloadTestCase: loadTestCase,
        refetchFiles: async () => {
            const fileResult = await getExecutionFilesForDropdown();
            if (fileResult?.status === 1 && fileResult.data) {
                dispatch(
                    setFileSuggestions({
                        suggestions: fileResult.data.suggestions || [],
                        descriptions: fileResult.data.descriptions || {},
                    })
                );
            }
        },
    };
}
